import { InputAdornment, Stack, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import useUnitProvider from '/@/core/context/unit';
import { _ } from '/@/locale';
import { setManagementZone as setManagementZoneAction } from '/@/store/nitrogen-recommendation/actions.js';
import { getManagementZone } from '/@/store/nitrogen-recommendation/selectors.js';

const DENSITY_LIMITS = {
    min: 40000,
    max: 120000,
};

const FieldLabel = ({ children }: { children: React.ReactNode }) => (
    <Typography variant='body2' color='primary' sx={{ fontWeight: 600 }}>
        {children}
    </Typography>
);

const ManagementZone = ({ id, type, minEnvironment, maxEnvironment, edit, cropMonitoring }) => {
    const units = useUnitProvider();
    const dispatch = useDispatch();

    const managementZoneFromStore = useSelector((state) => getManagementZone(state, id));
    const setManagementZone = bindActionCreators(setManagementZoneAction, dispatch);

    const managementZone = {
        ...(managementZoneFromStore || {}),
        id,
        type,
    };

    useEffect(() => {
        const data = { ...managementZone };
        if (type === 'fixed') {
            data['name'] = 'fixed';
        }
        data['valid'] = isValid(data);
        setManagementZone(data);
    }, []);

    useEffect(() => {
        if (cropMonitoring.id) {
            const udpatedZone = {
                ...managementZone,
                environment: cropMonitoring.environment,
                density: cropMonitoring.density,
                appliedNitrogen: cropMonitoring.fertilizer_amount,
            };
            const valid = isValid(udpatedZone);
            setManagementZone({
                ...udpatedZone,
                valid,
            });
        }
    }, cropMonitoring);

    const isValid = (managementZone) => {
        return (
            isValidEnvironment(managementZone.environment) &&
            isValidDensity(managementZone.density) &&
            managementZone.name &&
            managementZone.appliedNitrogen
        );
    };

    const update = (dataField, value) => {
        const data = { ...managementZone };
        if (type === 'fixed') {
            data['name'] = 'fixed';
        }
        data[dataField] = value;

        data['valid'] = isValid(data);
        setManagementZone(data);
    };

    const handleEnvironmentChanged = (value) => {
        update('environment', units.fromYield(value, 'corn'));
    };

    const handleDensityChanged = (value) => {
        update('density', units.fromDensity(value, 'corn'));
    };

    const handleAppliedNitrogenChanged = (value) => {
        update('appliedNitrogen', value);
    };

    const handleSoilNitrogenChanged = (value) => {
        update('soilNitrogen', value);
    };

    const handleNameChanged = (name) => {
        update('name', name);
    };

    const toDensity = (value) => {
        return units.toDensity(value, 'corn', 2).value;
    };

    const toYield = (value) => {
        return units.toYield(value, 'corn').value;
    };

    const isValidEnvironment = (value) => {
        const { min, max } = getEnvironmentLimits();
        const environment = toYield(value);
        return min <= environment && environment <= max;
    };

    const isValidDensity = (value) => {
        const { min, max } = getDensityLimits();
        const density = toDensity(value);
        return min <= density && density <= max;
    };

    const getEnvironmentLimits = () => {
        return {
            min: toYield(minEnvironment),
            max: toYield(maxEnvironment),
        };
    };

    const getDensityLimits = () => {
        const { min, max } = DENSITY_LIMITS;
        return {
            min: toDensity(min),
            max: toDensity(max),
        };
    };

    const envLimits = getEnvironmentLimits();
    const densityLimits = getDensityLimits();
    const totalNitrogen = (Number(managementZone.soilNitrogen) || 0) + (Number(managementZone.appliedNitrogen) || 0);

    const yieldUnit = units.getUnit('yield', 'corn');
    const densityUnit = units.getUnit('density', 'corn');

    return (
        <Grid
            container
            sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: type === 'variable' ? '1fr 1fr 1fr' : '1fr 1fr' },
                gap: 3,
                my: 2,
            }}
        >
            {type === 'variable' && (
                <Stack spacing={2}>
                    <FieldLabel>{_('Nombre')}</FieldLabel>
                    <TextField
                        fullWidth
                        placeholder={_('Escribir nombre')}
                        value={managementZone.name || ''}
                        onChange={(e) => handleNameChanged(e.target.value)}
                    />
                </Stack>
            )}

            <Stack spacing={2}>
                <FieldLabel>{_('Ambiente')}</FieldLabel>
                <TextField
                    type='number'
                    fullWidth
                    placeholder={`${_('Ej.')} ${units.toYield(10000, 'corn', 2).toString()}`}
                    value={toYield(managementZone.environment) || ''}
                    onChange={(e) => handleEnvironmentChanged(e.target.value)}
                    error={!!managementZone.environment && !isValidEnvironment(managementZone.environment)}
                    InputProps={{ endAdornment: <InputAdornment position='end'>{yieldUnit}</InputAdornment> }}
                    helperText={`${_('Rango de')} ${envLimits.min} ${_('a')} ${envLimits.max} (${yieldUnit})`}
                />
            </Stack>

            <Stack spacing={2}>
                <FieldLabel>{_('Densidad')}</FieldLabel>
                <TextField
                    type='number'
                    fullWidth
                    placeholder={`${_('Ej.')} ${units.toDensity(65000, 'corn', 2).toString()}`}
                    value={toDensity(managementZone.density) || ''}
                    onChange={(e) => handleDensityChanged(e.target.value)}
                    error={!!managementZone.density && !isValidDensity(managementZone.density)}
                    InputProps={{ endAdornment: <InputAdornment position='end'>{densityUnit}</InputAdornment> }}
                    helperText={`${_('Rango de')} ${densityLimits.min.toString()} a ${densityLimits.max.toString()} (${densityUnit})`}
                />
            </Stack>

            <Stack spacing={2}>
                <FieldLabel>{_('Nitrógeno aplicado en la siembra')}</FieldLabel>
                <TextField
                    type='number'
                    fullWidth
                    placeholder={`${_('Ej.')} ${units.toYield(100, 'corn', 2).toString()}`}
                    value={managementZone.appliedNitrogen || ''}
                    onChange={(e) => handleAppliedNitrogenChanged(e.target.value)}
                    inputProps={{ min: 0 }}
                    InputProps={{
                        endAdornment: <InputAdornment position='end'>{yieldUnit}</InputAdornment>,
                    }}
                />
            </Stack>

            <Stack spacing={2}>
                <FieldLabel>{_('Muestreo de nitrógeno en el suelo')}</FieldLabel>
                <TextField
                    type='number'
                    fullWidth
                    placeholder={`${_('Ej.')} ${units.toYield(120, 'corn', 2).toString()}`}
                    value={managementZone.soilNitrogen || ''}
                    onChange={(e) => handleSoilNitrogenChanged(e.target.value)}
                    inputProps={{ min: 0 }}
                    InputProps={{
                        endAdornment: <InputAdornment position='end'>{yieldUnit}</InputAdornment>,
                    }}
                    helperText={_(
                        'Valor del muestreo (profundidad 0-60cm del suelo) o valor de referencia en la zona.'
                    )}
                />
            </Stack>

            <Stack spacing={2}>
                <FieldLabel>{_('Nitrógeno total')}</FieldLabel>
                <TextField
                    type='number'
                    fullWidth
                    disabled
                    value={totalNitrogen.toFixed(2)}
                    InputProps={{
                        endAdornment: <InputAdornment position='end'>{yieldUnit}</InputAdornment>,
                    }}
                />
            </Stack>
        </Grid>
    );
};

export default ManagementZone;
